import teamService, {
  TeamContractRules,
  TeamListDto,
  defaultContractRules,
  isCqvKind,
} from '../services/teams/teamService';

const LEADERSHIP_ROLES = ['TeamLeader', 'Manager', 'Team Manager', 'TeamManager'];

export function normalizeUserId(id: string | null | undefined): string {
  return (id || '').toLowerCase();
}

export function getTeamMemberIds(team: TeamListDto): string[] {
  if (team.salespersons?.length) {
    return team.salespersons.map((s) => s.userId);
  }
  return team.salespersonsIds || [];
}

export function usuarioEhDoTimeCqv(times: TeamListDto[] | undefined, userId?: string | null): boolean {
  if (!times?.length || !userId) return false;
  const atual = normalizeUserId(userId);
  if (!atual) return false;
  return times.some((time) => {
    if (!isCqvKind(time.kind)) return false;
    if (normalizeUserId(time.managerId) === atual) return true;
    return getTeamMemberIds(time).some((id) => normalizeUserId(id) === atual);
  });
}

/**
 * Resolve o time cujas regras de contrato devem valer para o usuário.
 * Prioridade: time gerenciado (managerId) > time como vendedor/membro.
 */
export function resolveTeamForUser(
  teams: TeamListDto[],
  userId: string,
  userRoles: string[] = []
): TeamListDto | null {
  const currentNorm = normalizeUserId(userId);
  if (!currentNorm) return null;

  const isSalesFacingTeam = (t: TeamListDto) => !isCqvKind(t.kind);

  const managedTeam = teams.find(
    (t) => isSalesFacingTeam(t) && normalizeUserId(t.managerId) === currentNorm
  );
  if (managedTeam) return managedTeam;

  const memberTeam = teams.find((t) =>
    isSalesFacingTeam(t) &&
    getTeamMemberIds(t).some((id) => normalizeUserId(id) === currentNorm)
  );
  if (memberTeam) return memberTeam;

  const hasLeadershipRole = userRoles.some((role) => LEADERSHIP_ROLES.includes(role));
  if (hasLeadershipRole) {
    console.warn(
      '[teamContractRules] Usuário com role de liderança sem time associado:',
      userId
    );
  }

  return null;
}

export function mergeTeamContractRules(team: TeamListDto | null): TeamContractRules {
  if (!team?.contractRules) {
    return { ...defaultContractRules };
  }
  return { ...defaultContractRules, ...team.contractRules };
}

/**
 * Regras de contrato aplicáveis ao usuário autenticado.
 *
 * Usa `GET /teams/my-contract-rules`, que resolve a regra no servidor. A versão anterior baixava a
 * lista completa de times a cada carga da página de contratos e a cada abertura do modal de novo
 * contrato — três consultas pesadas por sessão para extrair uma única linha.
 *
 * `resolveTeamForUser` e `mergeTeamContractRules` continuam exportados para quem já tem a lista de
 * times em mãos e quer resolver localmente, sem uma requisição extra.
 */
export async function loadTeamContractRules(): Promise<TeamContractRules> {
  return teamService.getMyContractRules();
}
